import React from 'react';
import {
  Box,
  Grid,
  Typography,
  TextField,
  MenuItem,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Radio,
  RadioGroup,
  Select,
  Input,
  Chip,
  makeStyles,
} from '@material-ui/core';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import RoomOutlinedIcon from '@material-ui/icons/RoomOutlined';
import PhoneOutlinedIcon from '@material-ui/icons/PhoneOutlined';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import BusinessOutlinedIcon from '@material-ui/icons/BusinessOutlined';
import { formatPhone } from './config';

const UF_LIST = [
  'AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG',
  'PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO',
];

const SITUACOES = ['Ativo', 'Aposentado', 'Pensionista'];

const ORGAOS = ['Câmara dos Deputados', 'Senado Federal', 'TCU'];

const useStyles = makeStyles((theme) => ({
  title: {
    fontWeight: 700,
    textAlign: 'center',
    marginBottom: 4,
    color: '#1a3a5c',
  },
  subtitle: {
    textAlign: 'center',
    color: '#5a7a9a',
    marginBottom: theme.spacing(3),
  },
  field: {
    marginBottom: theme.spacing(1),
  },
  fieldLabel: {
    fontWeight: 600,
    fontSize: '0.875rem',
    color: '#333',
    marginBottom: 4,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
}));

function formatCPF(value) {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length <= 9)
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(
    6,
    9
  )}-${digits.slice(9)}`;
}

function formatDate(value) {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

const SELECT_MENU_PROPS = {
  PaperProps: {
    style: {
      maxHeight: 220,
      borderRadius: 8,
      marginTop: 4,
    },
  },
  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
  transformOrigin: { vertical: 'top', horizontal: 'left' },
  getContentAnchorEl: null,
};

function getCustomAnswerValue(customAnswers, question) {
  const found = (customAnswers || []).find((a) => a.id === question.id);
  if (!found) {
    return question.type === 5 ? [] : '';
  }
  if (question.type === 5) {
    return Array.isArray(found.answer) ? found.answer : [];
  }
  return found.answer ?? '';
}

function setCustomAnswer(customAnswers, question, value) {
  const next = (customAnswers || []).filter((a) => a.id !== question.id);
  next.push({
    id: question.id,
    questionText: question.text,
    type: question.type,
    answer: value,
  });
  return next;
}

function CustomQuestionField({ question, value, onChange, classes }) {
  const fieldLabel = (
    <Typography className={classes.fieldLabel}>
      {question.text} *
    </Typography>
  );

  if (question.type === 3) {
    return (
      <Grid item xs={12}>
        {fieldLabel}
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
        />
      </Grid>
    );
  }

  if (question.type === 1) {
    return (
      <Grid item xs={12}>
        {fieldLabel}
        <TextField
          fullWidth
          select
          variant="outlined"
          size="small"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
        >
          {question.options.map((opt) => (
            <MenuItem key={opt.id} value={opt.label}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
    );
  }

  if (question.type === 2) {
    const selected = Array.isArray(value) ? value : [];
    return (
      <Grid item xs={12}>
        {fieldLabel}
        <FormControl fullWidth variant="outlined" size="small">
          <Select
            multiple
            value={selected}
            onChange={(e) => onChange(e.target.value)}
            input={<Input />}
            renderValue={(picked) => (
              <Box display="flex" flexWrap="wrap" gridGap={4}>
                {picked.map((p) => (
                  <Chip key={p} label={p} size="small" />
                ))}
              </Box>
            )}
            MenuProps={SELECT_MENU_PROPS}
          >
            {question.options.map((opt) => (
              <MenuItem key={opt.id} value={opt.label}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
    );
  }

  if (question.type === 4) {
    return (
      <Grid item xs={12}>
        <FormControl component="fieldset">
          <FormLabel component="legend" style={{ marginBottom: 4 }}>
            <Typography className={classes.fieldLabel}>
              {question.text} *
            </Typography>
          </FormLabel>
          <RadioGroup
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
          >
            {question.options.map((opt) => (
              <FormControlLabel
                key={opt.id}
                value={opt.label}
                control={<Radio color="primary" />}
                label={opt.label}
              />
            ))}
          </RadioGroup>
        </FormControl>
      </Grid>
    );
  }

  if (question.type === 5) {
    const selected = Array.isArray(value) ? value : [];
    const toggle = (label) => {
      const next = selected.includes(label)
        ? selected.filter((s) => s !== label)
        : [...selected, label];
      onChange(next);
    };
    return (
      <Grid item xs={12}>
        <FormControl component="fieldset">
          <FormLabel component="legend" style={{ marginBottom: 4 }}>
            <Typography className={classes.fieldLabel}>
              {question.text} *
            </Typography>
          </FormLabel>
          <FormGroup>
            {question.options.map((opt) => (
              <FormControlLabel
                key={opt.id}
                control={
                  <Checkbox
                    checked={selected.includes(opt.label)}
                    onChange={() => toggle(opt.label)}
                    color="primary"
                  />
                }
                label={opt.label}
              />
            ))}
          </FormGroup>
        </FormControl>
      </Grid>
    );
  }

  return null;
}

export default function StepDadosPessoais({
  formData,
  updateFormData,
  customQuestions = [],
}) {
  const classes = useStyles();

  const handleCustomChange = (question, newValue) => {
    updateFormData(
      'custom_answers',
      setCustomAnswer(formData.custom_answers, question, newValue)
    );
  };

  return (
    <Box>
      <Typography variant="h5" className={classes.title}>
        Dados Pessoais
      </Typography>
      <Typography variant="body2" className={classes.subtitle}>
        Preencha seus dados básicos para a inscrição
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography className={classes.fieldLabel}>
            <PersonOutlineIcon fontSize="small" color="action" /> Nome Completo
            *
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            value={formData.name}
            onChange={(e) => updateFormData('name', e.target.value)}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Typography className={classes.fieldLabel}>CPF *</Typography>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            value={formData.cpf}
            onChange={(e) => updateFormData('cpf', formatCPF(e.target.value))}
            placeholder="000.000.000-00"
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Typography className={classes.fieldLabel}>
            Data de Nascimento *
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            value={formData.birthday}
            onChange={(e) =>
              updateFormData('birthday', formatDate(e.target.value))
            }
            placeholder="DD/MM/AAAA"
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Typography className={classes.fieldLabel}>
            <RoomOutlinedIcon fontSize="small" color="action" /> Cidade *
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            value={formData.city}
            onChange={(e) => updateFormData('city', e.target.value)}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Typography className={classes.fieldLabel}>Estado (UF) *</Typography>
          <TextField
            fullWidth
            select
            variant="outlined"
            size="small"
            value={formData.state}
            onChange={(e) => updateFormData('state', e.target.value)}
            SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
          >
            {UF_LIST.map((uf) => (
              <MenuItem key={uf} value={uf}>
                {uf}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Typography className={classes.fieldLabel}>
            <PhoneOutlinedIcon fontSize="small" color="action" /> Celular *
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            value={formData.phone}
            onChange={(e) =>
              updateFormData('phone', formatPhone(e.target.value))
            }
            placeholder="(00) 9 0000-0000"
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Typography className={classes.fieldLabel}>
            <MailOutlineIcon fontSize="small" color="action" /> E-mail *
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            type="email"
            value={formData.email}
            onChange={(e) => updateFormData('email', e.target.value)}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Typography className={classes.fieldLabel}>
            <BusinessOutlinedIcon fontSize="small" color="action" /> Situação
            Funcional *
          </Typography>
          <TextField
            fullWidth
            select
            variant="outlined"
            size="small"
            value={formData.situation}
            onChange={(e) => updateFormData('situation', e.target.value)}
            SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
          >
            {SITUACOES.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Typography className={classes.fieldLabel}>
            Órgão de Lotação *
          </Typography>
          <TextField
            fullWidth
            select
            variant="outlined"
            size="small"
            value={formData.organ}
            onChange={(e) => updateFormData('organ', e.target.value)}
            SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
          >
            {ORGAOS.map((o) => (
              <MenuItem key={o} value={o}>
                {o}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12}>
          <Typography className={classes.fieldLabel}>Matrícula *</Typography>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            value={formData.matricula}
            onChange={(e) => updateFormData('matricula', e.target.value)}
          />
        </Grid>

        {customQuestions.length > 0 && (
          <Grid item xs={12}>
            <Box mt={2} mb={1}>
              <Typography
                variant="subtitle1"
                style={{ fontWeight: 700, color: '#1a3a5c' }}
              >
                Informações adicionais
              </Typography>
            </Box>
          </Grid>
        )}

        {customQuestions.map((question) => (
          <CustomQuestionField
            key={question.id}
            question={question}
            value={getCustomAnswerValue(formData.custom_answers, question)}
            onChange={(newValue) => handleCustomChange(question, newValue)}
            classes={classes}
          />
        ))}
      </Grid>
    </Box>
  );
}
